import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Heart, Loader2, XCircle, Home } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function DonationSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [amount, setAmount] = useState(null);
  const pollCountRef = useRef(0);
  const maxPolls = 5;

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/donations/status/${sessionId}`);
        if (response.ok) {
          const data = await response.json();
          
          if (data.payment_status === 'paid') {
            setStatus('success');
            setAmount(data.amount);
            return true;
          } else if (data.status === 'expired') {
            setStatus('failed');
            return true;
          }
        }
        return false;
      } catch (error) {
        console.error('Status check error:', error);
        return false;
      }
    };

    const poll = async () => {
      const done = await checkStatus();
      if (!done && pollCountRef.current < maxPolls) {
        pollCountRef.current++;
        setTimeout(poll, 2000);
      } else if (!done) {
        // Max polls reached without success
        setStatus('success'); // Assume success if we got here from Stripe
        setAmount(null);
      }
    };

    poll();
  }, [sessionId]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--background)' }}
    >
      {/* Background gradient */}
      <div 
        className="fixed inset-0 opacity-30"
        style={{
          background: `radial-gradient(ellipse at 20% 50%, var(--primary) 0%, transparent 50%),
                       radial-gradient(ellipse at 80% 80%, var(--secondary) 0%, transparent 50%)`
        }}
      />

      <div 
        className="relative z-10 w-full max-w-md glass rounded-3xl p-8 text-center"
      >
        {status === 'checking' && (
          <>
            <Loader2 
              className="w-16 h-16 mx-auto mb-4 animate-spin" 
              style={{ color: 'var(--primary)' }} 
            />
            <h1 
              className="text-2xl font-bold mb-2"
              style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}
            >
              Processing...
            </h1>
            <p style={{ color: 'var(--text-muted)' }}>
              Confirming your donation
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div 
              className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)' }}
            >
              <CheckCircle className="w-10 h-10" style={{ color: '#22C55E' }} />
            </div>
            <h1 
              className="text-3xl font-bold mb-2"
              style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}
            >
              Thank You!
            </h1>
            {amount && (
              <p 
                className="text-4xl font-bold mb-4"
                style={{ fontFamily: 'Teko, sans-serif', color: 'var(--primary)' }}
              >
                ${amount}
              </p>
            )}
            <p className="mb-6" style={{ color: 'var(--text-muted)' }}>
              Your donation helps keep the music playing. We appreciate your support!
            </p>
            <div className="flex items-center justify-center gap-2 mb-6">
              <Heart className="w-5 h-5" style={{ color: '#FF6B6B' }} />
              <span style={{ color: 'var(--text-main)' }}>
                From DJ's & MC Live
              </span>
            </div>
          </>
        )}

        {status === 'failed' && (
          <>
            <div 
              className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)' }}
            >
              <XCircle className="w-10 h-10" style={{ color: '#EF4444' }} />
            </div>
            <h1 
              className="text-3xl font-bold mb-2"
              style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}
            >
              Payment Issue
            </h1>
            <p className="mb-6" style={{ color: 'var(--text-muted)' }}>
              There was an issue processing your donation. Please try again.
            </p>
          </>
        )}

        <button
          onClick={() => navigate('/')}
          className="flex items-center justify-center gap-2 mx-auto px-6 py-3 rounded-xl font-medium transition-all hover:scale-105"
          style={{ 
            background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
            color: 'white'
          }}
        >
          <Home className="w-4 h-4" />
          <span>Back to Player</span>
        </button>
      </div>
    </div>
  );
}
